import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatAUD, formatUnits, formatPct, formatDateAU } from '../../../shared/format';
import { QAEmpty } from './QAEmpty';
import { QAChartTooltip } from './QAChartTooltip';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import type { PortfolioSnapshot, PortfolioAccount } from '../../../shared/api';
import { MacquarieUploadDialog } from './MacquarieUploadDialog';
import { HoldingPriceCell } from './HoldingPriceCell';
import {
  summarise, accountRows, consolidatedRows, sortRows, filterRows, groupByClass, allocationVsTarget, holdingsCsv,
  sectorLabel, securityTypeLabel, accountTypeLabel,
  type HoldingRow, type HoldingsView, type Sort, type SortKey, type TargetInput,
} from '../../../shared/holdingsView';
import { assetClassLabel } from '../../../shared/portfolio';

const CLASS_COLOUR: Record<string, string> = {
  equity_au: '#B8431A', equity_intl: '#2F4636', property: '#A07A3F', alt: '#7A5A2E', fi: '#8A6418', cash: '#9C9088',
};
const colourFor = (bucket: string, i: number) => CLASS_COLOUR[bucket] ?? ['#3D3530', '#7A6E63'][i % 2];
const DRIFT_BAND = 0.05; // +/- 5 points is inside the band

const signed = (n: number) => `${n >= 0 ? '+' : '-'}${formatAUD(Math.abs(n))}`;
const signedPct = (n: number) => `${n >= 0 ? '+' : ''}${formatPct(n)}`;
const tone = (n: number) => (n >= 0 ? 'text-forest' : 'text-rust-deep');

function brisbaneToday(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Brisbane' }).format(new Date());
}

export function PortfolioTab({ portfolio, targets, clientName }: { portfolio: PortfolioSnapshot; targets?: TargetInput | null; clientName?: string }) {
  const [view, setView] = useState<HoldingsView>('account');
  const [sort, setSort] = useState<Sort>({ key: 'value', dir: 'desc' });
  const [q, setQ] = useState('');
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const [tradeMode, setTradeMode] = useState<Record<string, boolean>>({});
  const [uploadOpen, setUploadOpen] = useState(false);
  const qc = useQueryClient();
  const today = brisbaneToday();
  const refresh = useMutation({
    mutationFn: () => api('/api/integrations/market/refresh', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries(),
  });

  const summary = useMemo(() => (portfolio?.accounts?.length ? summarise(portfolio) : null), [portfolio]);
  const allocation = useMemo(() => (portfolio?.accounts?.length ? allocationVsTarget(portfolio, targets) : null), [portfolio, targets]);

  if (!portfolio || !portfolio.accounts || portfolio.accounts.length === 0 || !summary || !allocation) {
    return (
      <div className="mt-6">
        <MacquarieUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} context="portfolio" />
        <div className="flex justify-end mb-4">
          <button onClick={() => setUploadOpen(true)} className="btn-ghost text-sm">Upload platform report</button>
        </div>
        <QAEmpty inline title="No accounts yet." />
      </div>
    );
  }

  const onSort = (key: SortKey) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'code' || key === 'class' ? 'asc' : 'desc' }));

  function exportCsv() {
    const rows = view === 'account'
      ? portfolio.accounts.flatMap((a) => accountRows(portfolio, a))
      : consolidatedRows(portfolio);
    const blob = new Blob(['﻿' + holdingsCsv(sortRows(filterRows(rows, q), sort))], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `holdings-${(clientName ?? 'client').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${today}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="mt-6 space-y-6" data-testid="holdings-screen">
      <MacquarieUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} context="portfolio" />

      <SummaryStrip s={summary} />

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <AllocationPanel rows={allocation.rows} hasTargets={allocation.hasTargets} total={summary.total} />
        <ConcentrationPanel portfolio={portfolio} />
      </div>

      <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label="Holdings view">
        <div className="inline-flex border border-hairline" role="group" aria-label="Group holdings">
          {([['account', 'By account'], ['consolidated', 'Consolidated'], ['class', 'By asset class']] as const).map(([k, label]) => (
            <button key={k} type="button" aria-pressed={view === k} onClick={() => setView(k)}
              className={`px-3 py-1.5 text-[11px] uppercase font-bold tracking-widest ${view === k ? 'bg-ink text-paper' : 'text-ink-mute hover:text-ink'}`}>
              {label}
            </button>
          ))}
        </div>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter code, name, class or sector"
          aria-label="Filter holdings" className="qa-input text-sm w-full sm:w-72" />
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink-mute qa-mono" title="Oldest price used on this screen">
            Prices as at {summary.oldestPriceAsOf ? formatDateAU(summary.oldestPriceAsOf) : 'not yet priced'}
          </span>
          <button type="button" onClick={() => refresh.mutate()} className="btn-ghost text-sm" disabled={refresh.isPending}>
            {refresh.isPending ? 'Refreshing…' : 'Refresh prices'}
          </button>
          <button type="button" onClick={() => setUploadOpen(true)} className="btn-ghost text-sm">Upload report</button>
          <button type="button" onClick={exportCsv} className="btn-ghost text-sm">Export CSV</button>
        </div>
      </div>

      {view === 'account' && portfolio.accounts.map((a) => {
        const rows = sortRows(filterRows(accountRows(portfolio, a), q), sort);
        if (q && rows.length === 0) return null;
        const isOpen = !closed[a.id];
        return (
          <section key={a.id} className="qa-card" aria-label={a.name}>
            <AccountHeader a={a} total={summary.total} isOpen={isOpen}
              onToggle={() => setClosed((c) => ({ ...c, [a.id]: isOpen }))}
              trade={!!tradeMode[a.id]} onTrade={() => setTradeMode((m) => ({ ...m, [a.id]: !m[a.id] }))} />
            {isOpen && (
              <HoldingsTable rows={rows} sort={sort} onSort={onSort} trade={!!tradeMode[a.id]}
                footer={[{ label: 'Holdings', rows }, { label: 'Cash', cash: a.cashBalance }, { label: 'Account total', rows, cash: a.cashBalance, strong: true }]}
                total={summary.total} />
            )}
          </section>
        );
      })}

      {view === 'consolidated' && (() => {
        const rows = sortRows(filterRows(consolidatedRows(portfolio), q), sort);
        return (
          <section className="qa-card" aria-label="All holdings">
            <header className="px-5 py-4 flex items-baseline justify-between">
              <div>
                <p className="qa-eyebrow">Consolidated</p>
                <h3 className="qa-display text-lg">All holdings across {summary.accounts} account{summary.accounts === 1 ? '' : 's'}</h3>
              </div>
              <p className="qa-mono text-sm text-ink-mute">{rows.length} securities</p>
            </header>
            <HoldingsTable rows={rows} sort={sort} onSort={onSort} showAccounts trade={false}
              footer={[{ label: 'Holdings', rows }, { label: 'Cash (all accounts)', cash: summary.cash }, { label: 'Household total', rows, cash: summary.cash, strong: true }]}
              total={summary.total} />
          </section>
        );
      })()}

      {view === 'class' && (() => {
        const groups = groupByClass(filterRows(consolidatedRows(portfolio), q), summary.total);
        const target = new Map(allocation.rows.map((r) => [r.bucket, r.target]));
        return (
          <section className="qa-card" aria-label="Holdings by asset class">
            <HoldingsTable rows={[]} sort={sort} onSort={onSort} showAccounts trade={false} total={summary.total}
              groups={groups.map((g) => ({ ...g, rows: sortRows(g.rows, sort), target: target.get(g.assetClass) ?? null }))}
              footer={[{ label: 'Cash (all accounts)', cash: summary.cash }, { label: 'Household total', rows: groups.flatMap((g) => g.rows), cash: summary.cash, strong: true }]} />
          </section>
        );
      })()}

      <p className="text-xs text-ink-mute">
        Income is cash received over the last 12 months; yield is that income over today's value (grossed-up yield adds franking credits).
      </p>
    </div>
  );
}

/* ---------------- Summary ---------------- */

function SummaryStrip({ s }: { s: ReturnType<typeof summarise> }) {
  const items: Array<{ label: string; value: string; sub?: string; toneClass?: string }> = [
    { label: 'Portfolio value', value: formatAUD(s.total), sub: `${s.accounts} account${s.accounts === 1 ? '' : 's'} · ${s.securities} securities` },
    { label: 'Invested', value: formatAUD(s.invested), sub: `${formatPct(s.total > 0 ? s.invested / s.total : 0)} of portfolio` },
    { label: 'Cash', value: formatAUD(s.cash), sub: `${formatPct(s.total > 0 ? s.cash / s.total : 0)} of portfolio` },
    { label: 'Unrealised gain', value: signed(s.gain), sub: `${signedPct(s.returnPct)} on cost of ${formatAUD(s.costBase, { compact: true })}`, toneClass: tone(s.gain) },
    { label: 'Income, last 12 months', value: formatAUD(s.incomeTtm), sub: `${formatPct(s.yieldPct, 2)} yield · ${formatPct(s.grossedUpYieldPct, 2)} grossed up` },
    { label: 'Franking credits', value: formatAUD(s.frankingTtm), sub: s.losers ? `${s.losers} position${s.losers === 1 ? '' : 's'} below cost` : 'No positions below cost' },
  ];
  return (
    <div className="qa-card grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 divide-x divide-hairline-paper" data-testid="holdings-summary">
      {items.map((it) => (
        <div key={it.label} className="px-5 py-4 min-w-0">
          <p className="qa-stat__label">{it.label}</p>
          <p className={`qa-mono text-xl mt-1 ${it.toneClass ?? 'text-ink'}`}>{it.value}</p>
          {it.sub && <p className="text-xs text-ink-mute mt-0.5 truncate" title={it.sub}>{it.sub}</p>}
        </div>
      ))}
    </div>
  );
}

/* ---------------- Allocation vs target ---------------- */

function AllocationPanel({ rows, hasTargets, total }: { rows: ReturnType<typeof allocationVsTarget>['rows']; hasTargets: boolean; total: number }) {
  const chart = rows.filter((r) => r.value > 0).map((r, i) => ({ bucket: r.label, key: r.bucket, value: r.value, pct: r.actual, colour: colourFor(r.bucket, i) }));
  const outside = rows.filter((r) => r.drift != null && Math.abs(r.drift) > DRIFT_BAND).length;
  return (
    <div className="qa-card p-6 xl:col-span-3">
      <div className="flex items-baseline justify-between mb-4">
        <p className="qa-eyebrow">Asset allocation{hasTargets ? ' vs target' : ''}</p>
        {hasTargets && (
          <p className={`text-xs qa-mono ${outside ? 'text-rust-deep' : 'text-forest'}`}>
            {outside ? `${outside} class${outside === 1 ? '' : 'es'} outside ±5%` : 'All classes within ±5%'}
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-6 items-center">
        <div className="h-44" aria-hidden>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={chart} dataKey="value" nameKey="bucket" innerRadius={52} outerRadius={82} stroke="#FBF7EF" isAnimationActive={false}>
                {chart.map((c) => <Cell key={c.key} fill={c.colour} />)}
              </Pie>
              <Tooltip content={<QAChartTooltip valueFormat={(v, entry) => `${formatAUD(Number(v))}${entry.payload?.pct != null ? ` · ${formatPct(entry.payload.pct as number)}` : ''}`} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <table className="w-full text-sm" aria-label="Allocation by asset class">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-ink-mute qa-mono">
              <th className="text-left font-semibold pb-2">Class</th>
              <th className="text-right font-semibold pb-2">Value</th>
              <th className="text-right font-semibold pb-2">Actual</th>
              {hasTargets && <th className="text-right font-semibold pb-2">Target</th>}
              {hasTargets && <th className="font-semibold pb-2 pl-4 w-[34%]">Drift</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const out = r.drift != null && Math.abs(r.drift) > DRIFT_BAND;
              return (
                <tr key={r.bucket} className="border-t border-hairline-paper">
                  <td className="py-1.5">
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-2.5 h-2.5" style={{ background: colourFor(r.bucket, i) }} aria-hidden />
                      {r.label}
                    </span>
                  </td>
                  <td className="py-1.5 text-right qa-mono">{formatAUD(r.value, { compact: true })}</td>
                  <td className="py-1.5 text-right qa-mono">{formatPct(r.actual)}</td>
                  {hasTargets && <td className="py-1.5 text-right qa-mono text-ink-mute">{r.target == null ? '—' : formatPct(r.target)}</td>}
                  {hasTargets && (
                    <td className="py-1.5 pl-4">
                      <DriftBar drift={r.drift ?? 0} out={out} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-hairline">
              <td className="pt-2 font-medium">Total</td>
              <td className="pt-2 text-right qa-mono font-medium">{formatAUD(total, { compact: true })}</td>
              <td className="pt-2 text-right qa-mono">100.0%</td>
              {hasTargets && <td />}
              {hasTargets && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
      {!hasTargets && <p className="text-xs text-ink-mute mt-3">No target allocation recorded. Set targets on the client record to see drift.</p>}
    </div>
  );
}

function DriftBar({ drift, out }: { drift: number; out: boolean }) {
  const half = 0.2; // bar spans -20 to +20 points
  const w = Math.min(Math.abs(drift), half) / half * 50;
  return (
    <div className="flex items-center gap-2">
      <div className="relative h-2 flex-1 bg-ink/5" aria-hidden>
        <div className="absolute top-0 bottom-0 bg-forest/10" style={{ left: `${50 - (DRIFT_BAND / half) * 50}%`, width: `${(DRIFT_BAND / half) * 100}%` }} />
        <div className="absolute top-[-2px] bottom-[-2px] w-px bg-ink/40" style={{ left: '50%' }} />
        <div className={`absolute top-0 bottom-0 ${out ? 'bg-rust' : 'bg-brass'}`}
          style={drift >= 0 ? { left: '50%', width: `${w}%` } : { right: '50%', width: `${w}%` }} />
      </div>
      <span className={`qa-mono text-xs w-14 text-right ${out ? 'text-rust-deep font-semibold' : 'text-ink-mute'}`}>
        {drift >= 0 ? '+' : ''}{(drift * 100).toFixed(1)}
      </span>
    </div>
  );
}

/* ---------------- Concentration ---------------- */

function ConcentrationPanel({ portfolio }: { portfolio: PortfolioSnapshot }) {
  const rows = consolidatedRows(portfolio);
  const top = [...rows].sort((a, b) => b.value - a.value).slice(0, 6);
  const top10 = [...rows].sort((a, b) => b.value - a.value).slice(0, 10).reduce((s, r) => s + r.weight, 0);
  const losers = rows.filter((r) => r.gain < 0).sort((a, b) => a.gain - b.gain).slice(0, 3);
  const maxW = top[0]?.weight ?? 1;
  return (
    <div className="qa-card p-6 xl:col-span-2">
      <div className="flex items-baseline justify-between mb-4">
        <p className="qa-eyebrow">Largest positions</p>
        <p className="text-xs text-ink-mute qa-mono">Top 10 = {formatPct(top10)}</p>
      </div>
      <ul className="space-y-2">
        {top.map((r) => (
          <li key={r.code} className="text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate"><span className="font-semibold">{r.code}</span> <span className="text-ink-mute">{r.name}</span></span>
              <span className="qa-mono text-xs shrink-0">{formatPct(r.weight)}</span>
            </div>
            <div className="h-1.5 bg-ink/5 mt-1" aria-hidden>
              <div className="h-full" style={{ width: `${(r.weight / maxW) * 100}%`, background: colourFor(r.assetClass, 0) }} />
            </div>
          </li>
        ))}
      </ul>
      {losers.length > 0 && (
        <div className="mt-5 pt-4 border-t border-hairline-paper">
          <p className="qa-stat__label mb-2">Largest unrealised losses</p>
          <ul className="space-y-1 text-sm">
            {losers.map((r) => (
              <li key={r.code} className="flex justify-between gap-3">
                <span className="truncate"><span className="font-semibold">{r.code}</span> <span className="text-ink-mute">{r.name}</span></span>
                <span className="qa-mono text-xs text-rust-deep shrink-0">{signed(r.gain)} ({signedPct(r.returnPct)})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ---------------- Account header ---------------- */

function AccountHeader({ a, total, isOpen, onToggle, trade, onTrade }: {
  a: PortfolioAccount; total: number; isOpen: boolean; onToggle: () => void; trade: boolean; onTrade: () => void;
}) {
  const invested = a.holdings.reduce((s, h) => s + h.currentValue, 0);
  const gain = a.holdings.reduce((s, h) => s + h.unrealisedGainLoss, 0);
  const cost = a.holdings.reduce((s, h) => s + h.costBase, 0);
  const income = a.holdings.reduce((s, h) => s + (h.incomeTtm ?? 0), 0);
  const meta = [accountTypeLabel(a.type), a.platform, a.accountNumberTail ? `ending ${a.accountNumberTail}` : null].filter(Boolean).join(' · ');
  return (
    <header className="px-5 py-4 flex flex-wrap items-center gap-x-8 gap-y-3 border-b border-hairline-paper">
      <button type="button" aria-expanded={isOpen} onClick={onToggle} className="flex-1 min-w-[16rem] text-left group">
        <p className="qa-mono text-[10px] text-ink-mute uppercase tracking-widest">{meta}</p>
        <h3 className="qa-display text-lg group-hover:text-rust">
          <span aria-hidden className="inline-block w-4 text-ink-mute">{isOpen ? '▾' : '▸'}</span>{a.name}
        </h3>
      </button>
      <dl className="flex flex-wrap gap-x-8 gap-y-2 text-right">
        <HeaderStat label="Value" value={formatAUD(a.value)} sub={`${formatPct(total > 0 ? a.value / total : 0)} of portfolio`} />
        <HeaderStat label="Cash" value={formatAUD(a.cashBalance)} sub={formatPct(a.value > 0 ? a.cashBalance / a.value : 0)} />
        <HeaderStat label="Unrealised" value={signed(gain)} sub={signedPct(cost > 0 ? gain / cost : 0)} toneClass={tone(gain)} />
        <HeaderStat label="Income 12m" value={formatAUD(income)} sub={`${formatPct(invested > 0 ? income / invested : 0, 2)} yield`} />
      </dl>
      <button type="button" onClick={onTrade} aria-pressed={trade}
        className={`btn-ghost text-sm ${trade ? 'text-forest' : ''}`}
        title="Opens an external IRESS order ticket; nothing is placed automatically">
        {trade ? 'Done' : 'Trade'}
      </button>
    </header>
  );
}

function HeaderStat({ label, value, sub, toneClass }: { label: string; value: string; sub?: string; toneClass?: string }) {
  return (
    <div>
      <dt className="qa-stat__label">{label}</dt>
      <dd className={`qa-mono text-base ${toneClass ?? ''}`}>{value}</dd>
      {sub && <dd className="text-[11px] text-ink-mute">{sub}</dd>}
    </div>
  );
}

/* ---------------- Holdings table ---------------- */

interface FooterLine { label: string; rows?: HoldingRow[]; cash?: number; strong?: boolean }
interface Group { assetClass: string; label: string; rows: HoldingRow[]; value: number; weight: number; gain: number; income: number; target: number | null }

function HoldingsTable({ rows, groups, sort, onSort, showAccounts, trade, footer, total }: {
  rows: HoldingRow[]; groups?: Group[]; sort: Sort; onSort: (k: SortKey) => void; showAccounts?: boolean; trade: boolean;
  footer: FooterLine[]; total: number;
}) {
  const all = groups ? groups.flatMap((g) => g.rows) : rows;
  const maxWeight = Math.max(0.0001, ...all.map((r) => r.weight));
  const showPe = all.some((r) => r.holding?.peRatio != null);
  const showClass = !groups; // grouped view already heads each block with its class
  const cols = 10 + (showClass ? 1 : 0) + (showAccounts ? 1 : 0) + (showPe ? 1 : 0) + (trade ? 1 : 0);

  const Th = ({ k, label, right, title }: { k: SortKey; label: string; right?: boolean; title?: string }) => {
    const active = sort.key === k;
    return (
      <th className={right ? 'text-right' : ''} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} title={title}>
        <button type="button" onClick={() => onSort(k)} className={`uppercase tracking-[0.1em] ${active ? 'text-ink' : ''} hover:text-ink`}>
          {label}{active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
        </button>
      </th>
    );
  };

  const renderRow = (r: HoldingRow) => (
    <tr key={r.key}>
      <td className="min-w-[15rem]">
        <div className="flex items-baseline gap-2">
          <span className="font-semibold qa-mono text-[13px]">{r.code}</span>
          {securityTypeLabel(r.securityType) && (
            <span className="text-[9px] uppercase tracking-widest text-ink-mute border border-hairline px-1">{securityTypeLabel(r.securityType)}</span>
          )}
        </div>
        <div className="text-xs text-ink-mute leading-snug">{r.name}{sectorLabel(r.sector) ? ` · ${sectorLabel(r.sector)}` : ''}</div>
      </td>
      {showAccounts && (
        <td className="text-xs text-ink-mute" title={r.accountNames.join(', ')}>
          <span className="block max-w-[11rem] truncate">{r.accountNames.length === 1 ? r.accountNames[0] : `${r.accountNames.length} accounts`}</span>
        </td>
      )}
      {showClass && <td className="text-xs whitespace-nowrap">{assetClassLabel(r.assetClass)}</td>}
      <td className="num">{formatUnits(r.units)}</td>
      <td className="num">{r.holding ? <HoldingPriceCell h={r.holding} /> : formatAUD(r.price, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
      <td className="num font-medium">{formatAUD(r.value)}</td>
      <td className="num">
        <div className="flex items-center justify-end gap-2">
          <div className="w-12 h-1.5 bg-ink/5 hidden md:block" aria-hidden>
            <div className="h-full bg-brass" style={{ width: `${(r.weight / maxWeight) * 100}%` }} />
          </div>
          <span>{formatPct(r.weight)}</span>
        </div>
      </td>
      <td className="num text-ink-mute">{formatAUD(r.costBase)}</td>
      <td className={`num ${tone(r.gain)}`}>{signed(r.gain)}</td>
      <td className={`num ${tone(r.gain)}`}>{signedPct(r.returnPct)}</td>
      <td className="num">{r.incomeTtm > 0 ? formatAUD(r.incomeTtm) : <span className="text-ink-mute">—</span>}</td>
      <td className="num">{r.yieldPct != null ? formatPct(r.yieldPct, 2) : <span className="text-ink-mute">—</span>}</td>
      {showPe && <td className="num">{r.holding?.peRatio != null ? r.holding.peRatio.toFixed(1) : <span className="text-ink-mute">—</span>}</td>}
      {trade && (
        <td className="text-right whitespace-nowrap">
          <div className="flex justify-end gap-2">
            <a href={`iress://ticket?symbol=${encodeURIComponent(r.code)}&action=buy`}
              className="px-2 py-1 text-[10px] uppercase font-bold tracking-wider bg-forest/10 text-forest hover:bg-forest/20">Buy</a>
            <a href={`iress://ticket?symbol=${encodeURIComponent(r.code)}&action=sell`}
              className="px-2 py-1 text-[10px] uppercase font-bold tracking-wider bg-rust/10 text-rust hover:bg-rust/20">Sell</a>
          </div>
        </td>
      )}
    </tr>
  );

  // Footer: value / weight / cost / gain / return / income sit under their columns.
  const lead = 1 + (showAccounts ? 1 : 0) + (showClass ? 1 : 0) + 2; // security, [accounts], [class], units, price
  const tail = 1 + (showPe ? 1 : 0) + (trade ? 1 : 0); // yield, [pe], [trade]
  const footRow = (f: FooterLine) => {
    const hv = (f.rows ?? []).reduce((s, r) => s + r.value, 0);
    const value = hv + (f.cash ?? 0);
    const cost = (f.rows ?? []).reduce((s, r) => s + r.costBase, 0);
    const gain = (f.rows ?? []).reduce((s, r) => s + r.gain, 0);
    const income = (f.rows ?? []).reduce((s, r) => s + r.incomeTtm, 0);
    const hasRows = !!f.rows?.length;
    return (
      <tr key={f.label} className={f.strong ? 'font-semibold' : 'text-ink-mute'}>
        <td colSpan={lead} className={f.strong ? 'text-ink' : ''}>{f.label}</td>
        <td className="num">{formatAUD(value)}</td>
        <td className="num">{formatPct(total > 0 ? value / total : 0)}</td>
        <td className="num">{hasRows ? formatAUD(cost) : ''}</td>
        <td className={`num ${hasRows ? tone(gain) : ''}`}>{hasRows ? signed(gain) : ''}</td>
        <td className={`num ${hasRows ? tone(gain) : ''}`}>{hasRows && cost > 0 ? signedPct(gain / cost) : ''}</td>
        <td className="num">{hasRows && income > 0 ? formatAUD(income) : ''}</td>
        <td colSpan={tail} />
      </tr>
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="qa-table qa-holdings">
        <thead>
          <tr>
            <Th k="code" label="Security" />
            {showAccounts && <th>Account</th>}
            {showClass && <Th k="class" label="Class" />}
            <Th k="units" label="Units" right />
            <Th k="price" label="Price" right />
            <Th k="value" label="Value" right />
            <Th k="weight" label="Weight" right title="Share of the household portfolio, including cash" />
            <Th k="costBase" label="Cost base" right />
            <Th k="gain" label="Unrealised" right />
            <Th k="returnPct" label="Return" right />
            <Th k="income" label="Income 12m" right />
            <Th k="yield" label="Yield" right />
            {showPe && <th className="text-right">P/E</th>}
            {trade && <th className="text-right">Trade</th>}
          </tr>
        </thead>
        <tbody>
          {groups
            ? groups.map((g) => (
              <Fragment key={g.assetClass}>
                <tr className="qa-holdings__group">
                  <td colSpan={lead}>
                    <span className="font-semibold">{g.label}</span>
                    <span className="text-xs text-ink-mute ml-2">{g.rows.length} holding{g.rows.length === 1 ? '' : 's'}{g.target != null ? ` · target ${formatPct(g.target)}` : ''}</span>
                  </td>
                  <td className="num font-semibold">{formatAUD(g.value)}</td>
                  <td className="num font-semibold">{formatPct(g.weight)}</td>
                  <td />
                  <td className={`num ${tone(g.gain)}`}>{signed(g.gain)}</td>
                  <td />
                  <td className="num">{g.income > 0 ? formatAUD(g.income) : ''}</td>
                  <td colSpan={tail} />
                </tr>
                {g.rows.map(renderRow)}
              </Fragment>
            ))
            : rows.map(renderRow)}
          {all.length === 0 && (
            <tr><td colSpan={cols} className="text-center text-ink-mute py-6">No holdings match.</td></tr>
          )}
        </tbody>
        <tfoot className="qa-holdings__foot">
          {footer.map(footRow)}
        </tfoot>
      </table>
    </div>
  );
}
